import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { cn } from "../../../components/common/u1/cn";
import { HomeIcon, MenuIcon, CloseIcon } from "../../../assets/ui/icons";
import { Modal, SessionExpired } from "../../../components/common";
import { CommonToast } from "../../../components/common/Toast";
import {
  useGetCompanyEditAccessQuery,
  useGetCompanyQuery,
  useGetCompanyDetailsMutation,
  useGetCompanyRequestStatusMutation,
  useUpdateCompanyRequestStatusMutation,
} from "../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../store/Company/companySlice";
import { clearUserInfo } from "../../../store/auth/authSlice";

import { EditTopbar } from "../../../components/admin/u1/edit/EditTopbar";
import { EditSidebar, GROUP_ICONS } from "../../../components/admin/u1/edit/EditSidebar";
import { usePageScrollLock } from "../../../components/common/u1/usePageScrollLock";
import { EditStepHeader } from "../../../components/admin/u1/edit/EditStepHeader";
import { useCompletionDrafts } from "../../../components/admin/u1/edit/useCompletionDrafts";
import { AboutCompanySection } from "../../../components/admin/u1/edit/AboutCompanySection";
import { IndustrySection } from "../../../components/admin/u1/edit/IndustrySection";
import { AddressContactSection } from "../../../components/admin/u1/edit/AddressContactSection";
import { SocialMediaSection } from "../../../components/admin/u1/edit/SocialMediaSection";
import { LogoBannerSection } from "../../../components/admin/u1/edit/LogoBannerSection";
import { LicenseInformationSection } from "../../../components/admin/u1/edit/LicenseInformationSection";
import { TaxInformationSection } from "../../../components/admin/u1/edit/TaxInformationSection";
import { OwnershipStructureSection } from "../../../components/admin/u1/edit/OwnershipStructureSection";
import { PeopleSection } from "../../../components/admin/u1/edit/PeopleSection";
import {
  SIGNATORY_SPEC,
  PERSONNEL_SPEC,
} from "../../../components/admin/u1/edit/peopleSpecs";
import { RelationSection } from "../../../components/admin/u1/edit/RelationSection";
import {
  RELATED_ENTITY_SPEC,
  BUSINESS_RELATION_SPEC,
} from "../../../components/admin/u1/edit/relationSpecs";
import { BankingInformationSection } from "../../../components/admin/u1/edit/BankingInformationSection";
import { InsuranceSection } from "../../../components/admin/u1/edit/InsuranceSection";
import { AuditorInformationSection } from "../../../components/admin/u1/edit/AuditorInformationSection";
import { FinancialDocumentsSection } from "../../../components/admin/u1/edit/FinancialDocumentsSection";
import { UploadDocumentSection } from "../../../components/admin/u1/edit/UploadDocumentSection";
import { findDocumentSpec } from "../../../components/admin/u1/edit/documentSpecs";
import { SectionPlaceholder } from "../../../components/admin/u1/edit/SectionPlaceholder";
import { useEditNavigation } from "../../../components/admin/u1/edit/useEditNavigation";
import {
  TOTAL_SECTIONS,
  TOTAL_STEPS,
  findGroup,
  findSection,
  stepOf,
} from "../../../components/admin/u1/edit/editSections";
import { completedSections } from "../../../components/admin/u1/edit/sectionCompletion";
import { FRAME, RAIL } from "../../../components/admin/u1/edit/editLayout";
import "../../../components/common/u1/tokens.css";

/* u1 Company Edit Profile — TIP-644, previewed at /u1/edit/:id.
 *
 * The shell only: it loads the company, keeps the flow's position, and hands
 * the current section its three callbacks. Every form's rules live with that
 * form (useAboutSection, useIndustrySection), and the flow's position lives in
 * useEditNavigation.
 *
 * TRANSCRIBED from pages/admin/CompanyEditProfile.jsx — the company query, the
 * `currentEditingCompany` dispatch every section reads from, the session-expiry
 * modal, the logged-out redirect, the request-closing on Continue, and the
 * refetch after each move.
 *
 * The frame is a fixed-height task shell rather than a scrolling page: the
 * viewport is split into the top bar, the rail and the working area, and only
 * the working area scrolls. That is what keeps the action bar on screen the way
 * the design shows it, and it is why the height chain is explicit — h-screen,
 * then min-h-0 on every flex child that has to shrink instead of grow.
 *
 * Stage 1 covers About Company and Industry; the remaining seventeen sections
 * render SectionPlaceholder until their own stage lands.
 */
export default function CompanyEditProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const { currentEditingCompany } = useSelector((state) => state.company);

  const [isSessionExpired, setSessionExpired] = useState(false);
  /* Below lg the rail is a drawer — at 288px fixed it would leave no room for
     the form on a phone. */
  const [railOpen, setRailOpen] = useState(false);

  const [getCompanyDetails] = useGetCompanyDetailsMutation();
  const [getCompanyRequestStatus] = useGetCompanyRequestStatusMutation();
  const [updateCompanyRequestStatus] = useUpdateCompanyRequestStatusMutation();

  /* May this user edit THIS company? */
  const { isLoading: isCheckingAccess, isError: isAccessDenied } =
    useGetCompanyEditAccessQuery(id, { skip: !id });

  const {
    data,
    isLoading: isLoadingCompany,
    isError,
    error,
  } = useGetCompanyQuery(
    { companyId: id, token },
    /* Not fetched at all until editing is permitted, so a refused visitor
       never receives the company's data in the first place. */
    { skip: !id || isCheckingAccess || isAccessDenied }
  );

  const isLoading = isCheckingAccess || isLoadingCompany;

  /* Transcribed: an expired token opens the shared modal rather than dropping
     the viewer on a blank form. */
  useEffect(() => {
    if (isError && error?.data?.code === "token_not_valid") setSessionExpired(true);
  }, [isError, error]);

  const closeSessionExpired = () => {
    setSessionExpired(false);
    dispatch(clearUserInfo());
    navigate("/login");
  };

  /* Transcribed verbatim, including reading localStorage rather than the store
     — this page is reachable directly, before userInfo is rehydrated. */
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  useEffect(() => {
    if (!isLoggedIn) navigate("/");
  }, [isLoggedIn, navigate]);

  /* Refused by the server: leave, rather than sitting on an editor for
     somebody else's company. */
  useEffect(() => {
    if (isAccessDenied) {
      CommonToast.error("You do not have permission to edit this company.");
      navigate("/dashboard/mycompanies", { replace: true });
    }
  }, [isAccessDenied, navigate]);

  /* Transcribed: every section reads the company from the store, not from
     props, so the query result has to be pushed there. */
  useEffect(() => {
    if (data) dispatch(setCurrentEditingCompany(data));
  }, [data, dispatch]);

  /* Every list section's pending drafts, which the company record does not carry: a new row is written as a draft awaiting approval, so a section can be visibly... */
  const { extras: draftExtras, refresh: refreshDrafts } = useCompletionDrafts(
    /* Withheld until editing is permitted, for the same reason the company
       query is: a refused visitor should not receive this company's drafts. */
    isCheckingAccess || isAccessDenied ? undefined : id
  );

  /* Transcribed: after any move the company is re-read through the details
     mutation, which returns a fuller record than the list query. */
  const refreshCompany = async () => {
    try {
      const fresh = await getCompanyDetails({ companyId: id, token }).unwrap();
      if (fresh) dispatch(setCurrentEditingCompany(fresh));
      /* A saved row may be a draft, which the company record does not carry —
         so the rail's dots need the drafts re-read too, not just the company. */
      refreshDrafts();
    } catch {
      /* The live code has no catch here at all, so a failure became an
         unhandled rejection. The form keeps the record it already has, which
         is the last good one. */
    }
  };

  /* Transcribed: Continue closes the outstanding admin request when the latest
     one is Completed. Skip and Back deliberately do not. */
  const closeOutstandingRequest = async () => {
    try {
      const individualID =
        localStorage.getItem("userindividualID") ||
        data?.userCompany?.[0]?.individualID?.individualID;

      const res = await getCompanyRequestStatus({
        companyID: id,
        individualID,
        token,
      });

      const latest = res?.data?.length ? res.data[res.data.length - 1] : null;
      if (latest?.status === "Completed") {
        await updateCompanyRequestStatus({ requestID: latest.requestID, token });
      }
    } catch {
      /* Same as the live path: the move is not blocked by the bookkeeping. */
    }
  };

  const nav = useEditNavigation({
    onAdvance: closeOutstandingRequest,
    onVisit: refreshCompany,
  });

  const section = findSection(nav.section);
  const group = section && findGroup(section.group);

  const completed = completedSections(currentEditingCompany, draftExtras);

  /* The rail's own actions close the drawer on a phone, where a tap that left
     it open would hide the section it just moved to. */
  const selectSection = (label) => {
    nav.goTo(label);
    setRailOpen(false);
  };

  /* The design's floating shortcut, and the step heading, both scroll with the
     form — so they are handed to the section rather than pinned by the shell. */
  /* The flow is exactly the viewport and scrolls its own column, so the page
     behind it must not scroll too — see usePageScrollLock. */
  usePageScrollLock();

  const GroupIcon = GROUP_ICONS[group?.id];

  const header = (
    <div className="flex items-start justify-between gap-4">
      <EditStepHeader
        step={stepOf(nav.section)}
        totalSteps={TOTAL_STEPS}
        /* The eyebrow's own wording where a group has one — the rail's label
           and the step line are not always the same string. */
        groupTitle={group?.eyebrow || group?.title}
        /* Undefined for every group but Financial, which leaves
           EditStepHeader's flow-wide defaults in place. */
        title={group?.stepTitle}
        intro={group?.stepIntro}
      />

      <button
        type="button"
        onClick={() => navigate("/dashboard")}
        aria-label="Go to dashboard"
        className={cn(
          "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
          "bg-[var(--navy)] text-white transition-colors",
          "hover:bg-[var(--ink)] focus-visible:outline-none",
          "focus-visible:ring-2 focus-visible:ring-[var(--coral)] focus-visible:ring-offset-2"
        )}
      >
        {/* The group's own mark, the same one the rail shows for it. */}
        {GroupIcon ? <GroupIcon size={18} /> : <HomeIcon size={18} />}
      </button>
    </div>
  );

  /* Where the flow lets you out. One place, used by the last section's Finish
     and Submit, by its Skip, and by Save & exit. */
  const finishFlow = () => navigate("/dashboard/my-companies");

  const renderSection = () => {
    const common = {
      header,
      onNext: nav.next,
      onSkip: nav.skip,
      onPrev: nav.hasPrev ? nav.back : undefined,
    };

    if (nav.section === "About Business") {
      /* No Back on the first section — the live About.jsx has none either. */
      return (
        <AboutCompanySection
          header={header}
          onNext={common.onNext}
          onSkip={common.onSkip}
        />
      );
    }
    if (nav.section === "Industry") {
      return <IndustrySection {...common} />;
    }
    if (nav.section === "Address & Contact Information") {
      return <AddressContactSection {...common} />;
    }
    if (nav.section === "Social Media") {
      return <SocialMediaSection {...common} />;
    }
    if (nav.section === "Logo & Banner") {
      return <LogoBannerSection {...common} />;
    }
    if (nav.section === "License Information") {
      return <LicenseInformationSection {...common} />;
    }
    if (nav.section === "Tax Information") {
      return <TaxInformationSection {...common} />;
    }
    if (nav.section === "Ownership Structure") {
      return <OwnershipStructureSection {...common} />;
    }
    /* One component for both people sections — see peopleSpecs.js. */
    if (nav.section === "Authorized Signatory") {
      return <PeopleSection spec={SIGNATORY_SPEC} {...common} />;
    }
    if (nav.section === "Key Personnel") {
      return <PeopleSection spec={PERSONNEL_SPEC} {...common} />;
    }
    /* And one for both relation sections — see relationSpecs.js. */
    if (nav.section === "Related Entities") {
      return <RelationSection spec={RELATED_ENTITY_SPEC} {...common} />;
    }
    if (nav.section === "Business Relation") {
      return <RelationSection spec={BUSINESS_RELATION_SPEC} {...common} />;
    }
    if (nav.section === "Banking Information") {
      return <BankingInformationSection {...common} />;
    }
    if (nav.section === "Insurance") {
      return <InsuranceSection {...common} />;
    }
    if (nav.section === "Auditor Information") {
      return <AuditorInformationSection {...common} />;
    }
    if (nav.section === "Financial Documents") {
      return <FinancialDocumentsSection {...common} />;
    }
    /* One component for all three document sections — see documentSpecs.js. */
    if (findDocumentSpec(nav.section)) {
      const isLast = !nav.hasNext;
      return (
        <UploadDocumentSection
          {...common}
          isLast={isLast}
          onNext={isLast ? finishFlow : common.onNext}
          onSkip={isLast ? finishFlow : common.onSkip}
        />
      );
    }
    return (
      <SectionPlaceholder
        {...common}
        title={section?.navLabel || "Section"}
        companyId={id}
        onBack={common.onPrev}
      />
    );
  };

  /* `isAccessDenied` holds the skeleton too: rendering the editor now would
     flash it at somebody the redirect above is already sending away. */
  if (isLoading || isAccessDenied) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        className="u1-root flex min-h-screen items-center justify-center bg-[var(--bg)]"
      >
        <span className="sr-only">Loading this company profile…</span>
        <div aria-hidden="true" className="flex w-full max-w-[900px] flex-col gap-3 p-6">
          <span className="h-8 w-[280px] rounded bg-[var(--skeleton-hi)]" />
          <span className="h-[220px] w-full rounded-xl bg-[var(--skeleton-hi)]" />
        </div>
      </div>
    );
  }

  return (
    /* h-screen, not min-h-screen: the flow fills the viewport exactly and the
       CONTENT scrolls, not the page. A min-height also gave the rail nothing
       definite to resolve its own height against. */
    <div className="u1-root flex h-screen flex-col overflow-hidden bg-[var(--bg)]">
      {isSessionExpired && (
        <Modal modalHandle={closeSessionExpired}>
          <SessionExpired handleNavigate={closeSessionExpired} />
        </Modal>
      )}

      <EditTopbar
        company={currentEditingCompany || data}
        companyId={id}
        onHome={() => navigate("/")}
        /* The flow is entered from My Companies, so that is where leaving it
           returns. It does not save — see EditTopbar and F-043. */
        onSaveExit={finishFlow}
      />

      {/* The rail and the working area run edge to edge as one row. */}
      <div
        className={cn(
          FRAME,
          "u1-edit-body flex min-h-0 w-full flex-1"
        )}
      >
        {/* The rail, inline from lg — RAIL's width, and it needs the room. */}
        <aside
          className={cn(
            RAIL,
            "hidden h-full shrink-0 flex-col overflow-hidden",
            "border-r border-[var(--line)] bg-white lg:flex"
          )}
        >
          {/* The rail scrolls its own contents, so a long group list never
              pushes the flow's height around. */}
          <EditSidebar
            className="min-h-0 flex-1 overflow-y-auto px-5 py-6"
            activeSection={nav.section}
            openGroup={nav.openGroup}
            onToggleGroup={nav.toggleGroup}
            onSelectSection={selectSection}
            completed={completed}
            completedCount={completed.length}
            totalCount={TOTAL_SECTIONS}
          />
        </aside>

        {railOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            {/* The scrim closes it, and so does choosing a section —
                otherwise the drawer would sit over the section it just moved
                to. */}
            <button
              type="button"
              aria-label="Close sections"
              onClick={() => setRailOpen(false)}
              className="absolute inset-0 bg-[var(--scrim)]"
            />
            <div
              className={cn(
                RAIL,
                "relative flex h-full flex-col overflow-hidden border-r",
                "border-[var(--line)] bg-white"
              )}
            >
              <EditSidebar
                className="min-h-0 flex-1 overflow-y-auto px-5 py-6"
                activeSection={nav.section}
                openGroup={nav.openGroup}
                onToggleGroup={nav.toggleGroup}
                onSelectSection={selectSection}
                completed={completed}
                completedCount={completed.length}
                totalCount={TOTAL_SECTIONS}
              />
            </div>
          </div>
        )}

        {/* min-w-0 so a long company name cannot push the column wider than its share; w-full so it claims that share in the first place. */}
        <main
          className={cn(
            "grid h-full min-h-0 w-full min-w-0",
            "grid-rows-[auto_minmax(0,1fr)]"
          )}
        >
          {/* The drawer's handle. Hidden from lg up, where the rail is always
              on screen. */}
          <div className="shrink-0 border-b border-[var(--line)] bg-white px-4 py-2 lg:hidden">
            <button
              type="button"
              onClick={() => setRailOpen((open) => !open)}
              aria-expanded={railOpen}
              className={cn(
                "flex items-center gap-2 rounded-lg px-2 py-1.5",
                "text-[13px] font-semibold text-[var(--ink-2)] font-sans",
                "hover:bg-[var(--ghost-hover)] focus-visible:outline-none",
                "focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
              )}
            >
              {railOpen ? <CloseIcon size={15} /> : <MenuIcon size={15} />}
              Sections
            </button>
          </div>

          {/* Explicitly in row 2. The handle above is `lg:hidden`, and a grid item with `display:none` leaves the grid altogether — so from lg up the section was... */}
          <div className="row-start-2 grid min-h-0 w-full">
            {renderSection()}
          </div>
        </main>
      </div>
    </div>
  );
}
